import React, { useCallback, useId, useState } from "react";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Form,
  FormControl,
  type FormControlProps,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export function InlineEdit({
  value,
  name,
  onChangeCommit,
  validate,
  ...props
}: FormControlProps & {
  value: string;
  name?: string;
  // eslint-disable-next-line no-unused-vars
  onChangeCommit?: (newValue: string) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  validate?: (value: string) => string | true;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");
  const id = useId();

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        setIsEditing(false);
      }

      if (e.key === "Enter" || e.key === "Tab") {
        // Validate that the filename is valid, req.file.path.endsWith(".7z")
        const newValue = e.currentTarget.value;

        if (newValue === value) {
          // Value hasn't changed, no need to update
          setIsEditing(false);
          return;
        }

        if (validate) {
          const validationResult = validate?.(newValue);
          if (validationResult !== true) {
            setError(validationResult);
            e.currentTarget.setCustomValidity(validationResult);

            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }

        setError("");
        e.currentTarget.setCustomValidity("");

        // Commit change
        if (onChangeCommit) {
          try {
            await onChangeCommit(newValue);
          } catch (error) {
            // Handle failed updates
            console.warn("Failed to update:", error);
            setError("Failed to update");
            e.currentTarget.setCustomValidity("Failed to update");
            return;
          }
        }

        setIsEditing(false);
      }
    },
    [value, validate, onChangeCommit]
  );

  return isEditing ? (
    <>
      <Form action="#" onSubmit={(e) => e.preventDefault()} className="m-0 p-0">
        <Form.Group controlId={name || id}>
          <FormControl
            type="text"
            defaultValue={value}
            className={"d-block"}
            autoFocus
            name={name}
            {...props}
            isInvalid={!!error}
            aria-invalid={!!error}
            onFocus={(e) => {
              const target = e.target as HTMLInputElement;
              target?.select();
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setIsEditing(false);
              setError("");
            }}
          />
          <Form.Control.Feedback type="invalid" role="alert">
            {error}
          </Form.Control.Feedback>
        </Form.Group>
      </Form>
    </>
  ) : (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <span
        className="text-truncate"
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "bottom",
        }}
        title={value}
      >
        {value}
      </span>
      <InlineEditButton
        title="Edit"
        aria-label="Edit"
        onClick={() => setIsEditing(true)}
      />
    </div>
  );
}

function InlineEditButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
) {
  return (
    <Button variant="link" className="p-0" {...props}>
      <EditIcon />
    </Button>
  );
}

function EditIcon() {
  return <FontAwesomeIcon icon={faPencil} />;
}
